import React from 'react';
import gql from 'graphql-tag';
import { registerClass, currySC, IFNTheme, cns } from '@food/css-manager';
import { oldMediaToGraphqlMedia } from '../../utils/data';
import { DefaultLayout } from '../../utils/layout';
import { Query } from 'react-apollo';
import dayjs from 'dayjs';
import { Magazine as MagazineType } from '../../server-types';
import { canView } from '../../utils/canView';
import { isLogged } from '@food/auth/build/dist/logic/auth';
import { Spinner } from '../Spinner';
import { useTranslation } from 'react-i18next';
import { magazineCardFragment } from '../../utils/fragments';
import { useUserMagazineData } from '../../utils/hooks';
import { ILocation } from '../DefaultPageData';
import { TUserExtendedData } from '../UserExtendedData';
import { NotVisibleAction } from '../NotVisibleAction';


const magazineDetailQuery = gql`
	query MagazineDetailQuery($magazineId: ID!) {
		magazine: node(id: $magazineId) {
			... on Magazine {
				id
				name
				title
				category
				issueNumber
				numOfTheYear
				publicationDate
				numPages
				featuredImageSizes
				featuredImageUrl
				customVisibilityType
				customVisibilityArgs
				previewUrl
				prefixPages
				dropboxPath
				featuredImageMedia {
					id
					smallThumbUrl
					mediumThumbUrl
				}
				Media {
					id
					mediumThumbUrl
				}
				magazineContents {
					id
					name
					pageNumber
				}
				MagazineCollection {
					id
					name
					BrandSf
					startYear
					visibilityType
					visibilityArgs
				}
			}
		}
		magazines {
			edges {
				node {
					...MagazineCardFragment
				}
			}
		}
	}
	${magazineCardFragment}
`;

const styleClass = registerClass(
	(t: IFNTheme) => `

	.${sc('section')} {
		padding: ${t.ratios.xl * t.font.baseSize}px 0 ${t.ratios.l * t.font.baseSize}px;
	}

	.${sc('header')} {
		display: flex;
		align-items: center;
		justify-content: center;
		
		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			flex-wrap: wrap;
		}
	}

	.${sc('imageContainer')} {
		margin-right: ${t.ratios.xl * t.font.baseSize}px;
		flex-shrink: 0;
		max-width: 100%;
		
		img {
			max-width: ${20 * t.font.baseSize}px;  // AP Numerico, ci vuole una misura massima
			width: 100%;
			box-shadow: ${t.boxShadow(1)};
		}
		
		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			margin: ${t.ratios.xl * t.font.baseSize}px 0;
		
			&.${sc('layout', DefaultLayout.TABLET)} {
				padding: 0 15%;
			}
		
			&.${sc('layout', DefaultLayout.MOBILE)} {
				padding: 0 10%;
			}
		}
	}
	
	.${sc('infoContainer')} {
		max-width: 600px;

		&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
			text-align: center;
		}
	}

	.${sc('title')} {
		font-size: ${t.ratios.xl}em;
		font-weight: ${t.font.weights.bold};
		margin-bottom: ${t.ratios.l * t.font.baseSize}px;
	}
	
	.${sc('infoText')} {
		margin-bottom: ${t.ratios.xs * t.font.baseSize}px;
		font-weight: ${t.font.weights.semibold};
		line-height: ${t.ratios.xl};
	}

	.${sc('notVisibleAction')} {
		margin-top: ${t.ratios.l}em;
	}
	
	.${sc('layoutSwitcher')} {
	
		&.${sc('layout', DefaultLayout.MOBILE)} {
			display: flex;
			justify-content: center;
			white-space: pre;
		}
	}
	
	.${sc('button')} {
		padding: ${(t.ratios.xs / 3) * 2 * t.font.baseSize}px ${t.ratios.xl * t.font.baseSize}px;
		border-radius: ${t.radius * t.font.baseSize}px;
		font-weight: ${t.font.weights.bold};
		color: ${t.colors.white};
		display: inline-flex;
		margin-top: ${t.ratios.xl * t.font.baseSize}px;
	}
	
	.${sc('buttonYellow')} {
		background-color: ${t.colors.alt2.plain};
	}
	
	.${sc('buttonGreen')} {
		background-color: ${t.colors.success.plain};
	}

	.${sc('buttonBlue')} {
		background-color: ${t.colors.alt.dark};
	}
	
	.${sc('mainTitle')} {
		margin-bottom: ${t.ratios.l * t.font.baseSize}px;
	
		> h2 {
			font-size: ${t.ratios.xl}em;
			font-weight: ${t.font.weights.bold};
		}
		
		> .${sc('infolink')} {
			line-height: 1.4;
		}
	}
	
	.${sc('tabContainer')} {

		.${sc('tabSection')} {
			font-size: ${t.ratios.l}em;
			margin-top: ${t.ratios.s * t.font.baseSize}px;
			padding-left: ${t.ratios.s * t.font.baseSize}px;
			
			&:first-child {
				margin-top: 0;
			}

			a, span {
				display: flex;
				
				.${sc('tabTitle')} {
					line-height: 1.2;
				}

				.${sc('tabNumber')} {
					color: ${t.colors.grey.plain};
					display: inline-block;
					min-width: 2.3em;
					flex-grow: 0;
					flex-shrink: 0;
					text-align: right;
					line-height: 1.2;
					padding-right: 0.5em;

					&:after {
						content: ".";
						display: inline;
					}
				}

			}
		}
	}
  `,
);

const sc = currySC(styleClass);

export interface ILandingPageMagazineIndexData {
	layout: any;
	user?: TUserExtendedData | null;
	history?: any;
	location?: ILocation;
	magazineId?: string;
	textColor?: string;
	showIndex?: boolean;
}


export const LandingPageMagazineIndex: React.FC<ILandingPageMagazineIndexData> = ({
	layout,
	magazineId,
	textColor,
	showIndex
}) => {
	const { t } = useTranslation();
	const { loading: loading1, userMagazineData } = useUserMagazineData();

	if (magazineId) {
		return (
			<Query<{ magazine: MagazineType }, { magazineId: string }>
				query={magazineDetailQuery}
				variables={{ magazineId }}
			>
				{({ error, loading, data }) => {
					if (error) {
						console.error(error);
						return <span>error</span>;
					}

					if (loading1 || loading || !data) {
						return <Spinner />;
					}

					const { magazine } = data;

					console.log(data);
					const {
						featuredImageMedia,
						numPages,
						magazineContents,
						issueNumber,
						name,
						id,
						featuredImageSizes,
						numOfTheYear,
						publicationDate,
						MagazineCollection: collection,
					} = magazine;

					let dropboxPath = magazine.dropboxPath;
					if (dropboxPath) {
						if (dropboxPath.indexOf("?") > -1) {
							if (dropboxPath.indexOf("dl=") == -1) {
								dropboxPath = dropboxPath + "&dl=1";
							}
						} else {
							dropboxPath = dropboxPath + "?dl=1";
						}
					}

					const media =
						featuredImageMedia || oldMediaToGraphqlMedia(featuredImageSizes);
					const date = dayjs(publicationDate);
					const publicationYear =
						collection && collection.startYear
							? date.get('year') - collection.startYear
							: null;
					const issue = (issueNumber ? `N.${issueNumber} ` : '') + name;

					const visibilityArgs = {
						visibility: magazine.customVisibilityType || collection.visibilityType,
						args: magazine.customVisibilityType
							? magazine.customVisibilityArgs
							: collection.visibilityArgs,
						user: userMagazineData,
						isLogged: isLogged(),
					};

					const hasVisibility = canView(visibilityArgs);
					//const isSubscribed = visibilityArgs.user ? hasSubscription(visibilityArgs) : false;

					return (
						<div className={styleClass} style={{ 'color': textColor ? textColor : '#000' }}>

							<header
								className={cns(sc('header'), sc('layout', layout), sc('section'))}
							>
								<div className={cns(sc('imageContainer'), sc('layout', layout))}>
									{media && (
										<img
											src={media.mediumThumbUrl}
											alt={'magazine cover thumbnail'}
										/>
									)}
								</div>

								<div className={cns(sc('infoContainer'), sc('layout', layout))}>
									<div className={sc('title')}>{issue}</div>
									<div className={sc('infoText')}>
										{t(collection.BrandSf)} - {collection.name}
									</div>
									{collection.startYear && numOfTheYear && (
										<div className={sc('infoText')}>
											{t`Year`} {publicationYear} - {t`number_short_notation`}{' '}
											{numOfTheYear}
										</div>
									)}
									<div className={sc('infoText')}>
										{date.format('MM/YYYY')} | {numPages}{' '}
										{t`page_short_notation`}
									</div>

									{hasVisibility && dropboxPath && (
										<a
											className={cns(sc('button'), sc('buttonBlue'))}
											href={dropboxPath}
											style={textColor ? { 'backgroundColor': textColor } : {}}
											target='_blank'
										>
											{t`download`}
										</a>
									)}
									{!hasVisibility && (
										<NotVisibleAction
											visibilityArgs={visibilityArgs}
											magazine={magazine}
											layout={layout}
										/>
									)}
								</div>
							</header>

							{showIndex &&
								<main className={sc('mainIndex')}>
									{magazineContents && magazineContents.length > 0 && (
										<>
											<section className={sc('section')}>
												<div className={sc('mainTitle')}>
													<h2>{t`index`}</h2>
												</div>

												<div className={sc('tabContainer')}>
													{magazineContents.map((content: any) => (
														<div
															key={id}
															className={cns(
																sc('tabSection'),
																sc('text'),
																sc('layout', layout),
															)}
														>
															<span>
																<span className={sc('tabNumber')} style={{ 'color': textColor ? textColor : '#000', opacity: 0.6 }}>
																	{content.pageNumber}
																</span>
																<span className={sc('tabTitle')}>
																	{content.name}
																</span>
															</span>
														</div>
													))}
												</div>
											</section>
										</>
									)}
								</main>
							}
						</div>
					);
				}}
			</Query >
		);
	} else {
		return (<div></div>);
	}
};
